import { useState, useEffect } from 'react';
import {
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  CircularProgress,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import moment from 'moment-timezone';
import { useAuthStore, useSettingsStore } from '../../stores/root_store';

interface ZoneInfo {
  name: string;
  offset: number;
}

function formatZoneName(zone: ZoneInfo): string {
  const name = zone.name.replace(/_/g, ' ');
  const offsetHours = zone.offset / 60;
  const sign = offsetHours >= 0 ? '+' : '';
  return `${name} (${sign}${offsetHours})`;
}

export default function TimeZoneSettings() {
  const authStore = useAuthStore();
  const settingsStore = useSettingsStore();
  const [saving, setSaving] = useState(false);
  const [currentZone, setCurrentZone] = useState(
    settingsStore.settings?.profile?.timezone_city || ''
  );

  // Get zones for different countries
  const usZones = moment.tz
    .zonesForCountry('US', true)
    .filter(
      (zone) =>
        !zone.name.startsWith('America/Indiana') &&
        !zone.name.startsWith('America/North_Dakota') &&
        !zone.name.startsWith('America/Kentucky')
    )
    .sort((a, b) => {
      // Put common US zones first
      const common = [
        'America/New_York',
        'America/Los_Angeles',
        'America/Chicago',
        'America/Denver',
      ];
      if (common.includes(a.name)) return -1;
      if (common.includes(b.name)) return 1;
      return a.offset - b.offset;
    });

  const caZones = moment.tz.zonesForCountry('CA', true).sort((a, b) => a.offset - b.offset);
  const ukZones = moment.tz.zonesForCountry('GB', true).sort((a, b) => a.offset - b.offset);

  // Update currentZone when settings are loaded
  useEffect(() => {
    if (settingsStore.settings?.profile?.timezone_city && !currentZone) {
      setCurrentZone(settingsStore.settings.profile.timezone_city);
    }
  }, [settingsStore.settings?.profile?.timezone_city]);

  // Auto-detect timezone if not set
  useEffect(() => {
    if (!authStore.user?.uuid) return;
    if (!settingsStore.settings?.profile?.timezone_city && !currentZone) {
      const guessed = moment.tz.guess();
      if (guessed) {
        handleTimeZoneChange(guessed);
      }
    }
  }, [authStore.user?.uuid, settingsStore.settings?.profile?.timezone_city]);

  async function handleTimeZoneChange(zone: string) {
    if (!zone || saving) return;
    setCurrentZone(zone);
    setSaving(true);

    try {
      await settingsStore.updateSettings({ timezone_city: zone });
    } catch (err) {
      console.error('Failed to update timezone:', err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Set your timezone</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Timezone</InputLabel>
          <Select
            value={currentZone}
            label="Timezone"
            onChange={(e) => handleTimeZoneChange(e.target.value)}
          >
            <ListSubheader>USA</ListSubheader>
            {usZones.map((zone) => (
              <MenuItem key={zone.name} value={zone.name}>
                {formatZoneName(zone)}
              </MenuItem>
            ))}

            <ListSubheader>Canada</ListSubheader>
            {caZones.map((zone) => (
              <MenuItem key={zone.name} value={zone.name}>
                {formatZoneName(zone)}
              </MenuItem>
            ))}

            <ListSubheader>UK</ListSubheader>
            {ukZones.map((zone) => (
              <MenuItem key={zone.name} value={zone.name}>
                {formatZoneName(zone)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {saving ? <CircularProgress size={20} /> : <CheckIcon color="success" fontSize="small" />}
      </Stack>
    </Stack>
  );
}
